import { minimumGroupSize } from "./manager-insights.js";

export type KnowledgeDomainId =
  | "klassenmanagement"
  | "gespreksvaardigheden"
  | "soft-skills"
  | "werkvormen"
  | "pedagogiek";

type ChatThemeStat = {
  id: string;
  title: string;
  questions: number;
};

type ChatDomainStat = {
  id: KnowledgeDomainId;
  title: string;
  questions: number;
  themes: ChatThemeStat[];
};

/** School-wide question counts for this month; never stored per person. */
const chatDomains: readonly ChatDomainStat[] = [
  {
    id: "klassenmanagement",
    title: "Klassenmanagement",
    questions: 26,
    themes: [
      { id: "lesstart", title: "Een rustige lesstart", questions: 12 },
      { id: "routines", title: "Overgangen en routines", questions: 9 },
      { id: "escalatie", title: "Escalatie in de klas", questions: 5 },
    ],
  },
  {
    id: "pedagogiek",
    title: "Pedagogiek en instructie",
    questions: 22,
    themes: [
      { id: "instructie", title: "Instructie geven", questions: 10 },
      { id: "differentiatie", title: "Differentiatie in de les", questions: 7 },
      { id: "onrust", title: "Omgaan met onrust", questions: 5 },
    ],
  },
  {
    id: "gespreksvaardigheden",
    title: "Gespreksvaardigheden",
    questions: 15,
    themes: [
      { id: "ouders", title: "Gesprek met ouders", questions: 8 },
      { id: "feedback", title: "Feedback geven", questions: 7 },
    ],
  },
  {
    id: "werkvormen",
    title: "Praktische werkvormen",
    questions: 11,
    themes: [
      { id: "samenwerken", title: "Samenwerkend leren", questions: 6 },
      { id: "activeren", title: "Voorkennis activeren", questions: 5 },
    ],
  },
  {
    id: "soft-skills",
    title: "Soft skills en reflectie",
    questions: 10,
    themes: [
      { id: "reflectie", title: "Reflectie op eigen handelen", questions: 5 },
      { id: "collegas", title: "Collegiale afstemming", questions: 5 },
    ],
  },
];

export type ThemeBreakdown = {
  id: string;
  title: string;
  share: number;
};

export type DomainBreakdown = {
  id: KnowledgeDomainId;
  title: string;
  questions: number;
  share: number;
  themes: ThemeBreakdown[];
};

export function sharesOf(parts: readonly { id: string; amount: number }[]): Map<string, number> {
  const total = parts.reduce((sum, part) => sum + part.amount, 0);
  if (total <= 0) return new Map(parts.map((part) => [part.id, 0]));
  const exact = parts.map((part) => {
    const raw = (part.amount / total) * 100;
    const floor = Math.floor(raw);
    return { id: part.id, floor, remainder: raw - floor };
  });
  let leftover = 100 - exact.reduce((sum, part) => sum + part.floor, 0);
  const ranked = [...exact].sort(
    (left, right) => right.remainder - left.remainder || left.id.localeCompare(right.id),
  );
  const bonus = new Set<string>();
  for (const part of ranked) {
    if (leftover <= 0) break;
    bonus.add(part.id);
    leftover -= 1;
  }
  return new Map(exact.map((part) => [part.id, part.floor + (bonus.has(part.id) ? 1 : 0)]));
}

export function domainBreakdown(): DomainBreakdown[] {
  const domainShares = sharesOf(chatDomains.map((domain) => ({ id: domain.id, amount: domain.questions })));
  return chatDomains.map((domain) => {
    const themeShares = sharesOf(domain.themes.map((theme) => ({ id: theme.id, amount: theme.questions })));
    return {
      id: domain.id,
      title: domain.title,
      questions: domain.questions,
      share: domainShares.get(domain.id) ?? 0,
      themes: domain.themes
        .filter((theme) => theme.questions >= minimumGroupSize)
        .map((theme) => ({
          id: theme.id,
          title: theme.title,
          share: themeShares.get(theme.id) ?? 0,
        })),
    };
  });
}

export function domainQuestionTotal(): number {
  return chatDomains.reduce((total, domain) => total + domain.questions, 0);
}
